import { Link, usePage } from "@inertiajs/react"
import { FileText, MailPlus } from "lucide-react"

type Props = {
  flash?: {
    notice?: string
    alert?: string
  }
  city: {
    id: number
    name: string
    slug: string
  }
  meeting: {
    id: number
    title: string
    published_at: string | null
    permalink: string
    youtube_video_id: string | null
  }
  newsletter: {
    city_name: string
    signup_action: string
  }
  summaries: {
    public_html: string
    terse_text: string
    key_takeaways: string[]
  }
  recent_dispatches: Array<{
    id: number
    title: string
    published_at: string | null
    preview_text: string
  }>
}

export default function MeetingsShow({ city, meeting, newsletter, summaries, recent_dispatches }: Props) {
  const page = usePage<Props>()
  const flash = page.props.flash

  return (
    <main className="ch-noise relative z-10 mx-auto flex w-full max-w-[1280px] flex-1 px-4 py-12 md:px-12">
      <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <article className="lg:col-span-8 lg:border-r lg:border-[hsl(var(--ch-muted))] lg:pr-12">
          <Link className="mb-6 inline-block text-sm text-[hsl(var(--ch-primary))] underline underline-offset-4" href={`/cities/${city.slug}`}>
            Back to {city.name}
          </Link>
          <header className="mb-10">
            <h1 className="ch-masthead mb-6 text-[hsl(var(--ch-primary))]">{meeting.title}</h1>
            <div className="border-y-2 border-[hsl(var(--ch-primary))] py-4">
              <p className="ch-label-caps mb-1 text-[hsl(var(--ch-muted))]">Date of Entry</p>
              <p className="ch-headline-md text-[hsl(var(--ch-primary))]">
                {meeting.published_at
                  ? new Date(meeting.published_at).toLocaleDateString(undefined, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })
                  : "Unknown date"}
              </p>
            </div>
          </header>

          <section className="mt-2 border border-[hsl(var(--ch-muted))] bg-[hsl(var(--ch-surface-low))] p-6">
            <h2 className="ch-label-caps mb-3 text-[hsl(var(--ch-primary))]">Key Takeaways</h2>
            {summaries.key_takeaways.length > 0 ? (
              <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                {summaries.key_takeaways.map((takeaway, index) => (
                  <li key={`${index}-${takeaway.slice(0, 24)}`}>{takeaway}</li>
                ))}
              </ul>
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                {summaries.terse_text}
              </pre>
            )}
          </section>

          <section className="prose prose-slate mt-10 max-w-none text-[18px] leading-relaxed [&_p]:my-5">
            <div dangerouslySetInnerHTML={{ __html: summaries.public_html }} />
          </section>

        </article>

        <aside className="space-y-10 lg:col-span-4">
          <section className="border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface))] p-6">
            <MailPlus className="mb-4 h-8 w-8 text-[hsl(var(--ch-primary))]" />
            <h2 className="ch-headline-md mb-2 text-[hsl(var(--ch-primary))]">The {newsletter.city_name} Dispatch</h2>
            <p className="mb-5 text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
              Get short local updates without watching every council meeting.
            </p>
            {flash?.notice ? (
              <p className="mb-4 border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">{flash.notice}</p>
            ) : null}
            {flash?.alert ? (
              <p className="mb-4 border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">{flash.alert}</p>
            ) : null}
            <form action={newsletter.signup_action} className="space-y-3" method="post">
              <input
                name="authenticity_token"
                type="hidden"
                value={document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""}
              />
              <label className="ch-label-caps mb-1 block text-[hsl(var(--ch-primary))]" htmlFor="meeting-newsletter-email">
                Email Address
              </label>
              <input
                className="w-full border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-3 text-sm text-[hsl(var(--ch-primary))] placeholder:text-[hsl(var(--ch-muted))] focus:outline-none"
                id="meeting-newsletter-email"
                name="email"
                placeholder="citizen@example.com"
                required
                type="email"
              />
              <button
                className="ch-label-caps w-full border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-primary))] py-3 text-white transition-colors hover:bg-[hsl(var(--ch-bg))] hover:text-[hsl(var(--ch-primary))]"
                type="submit"
              >
                Receive Dispatch
              </button>
            </form>
          </section>

          <section>
            <h2 className="ch-label-caps mb-6 bg-[hsl(var(--ch-primary))] px-3 py-2 text-white">Recent Dispatches</h2>
            <div className="space-y-6">
              {recent_dispatches.map((dispatch) => (
                <Link
                  key={dispatch.id}
                  className="block rounded-sm border-b border-[hsl(var(--ch-muted))] p-2 pb-6 transition-colors hover:bg-[hsl(var(--ch-surface))]"
                  href={`/cities/${city.slug}/reports/${dispatch.id}-${dispatch.title.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                >
                  <p className="ch-label-caps mb-2 text-[hsl(var(--ch-muted))]">
                    {dispatch.published_at ? new Date(dispatch.published_at).toLocaleDateString() : "Undated"}
                  </p>
                  <h3 className="ch-headline-md mb-2 text-[hsl(var(--ch-primary))] hover:underline">{dispatch.title}</h3>
                  <p className="text-sm leading-relaxed text-[hsl(var(--ch-muted))]">{dispatch.preview_text}</p>
                </Link>
              ))}
              {recent_dispatches.length === 0 ? (
                <p className="text-sm text-[hsl(var(--ch-muted))]">No recent dispatches yet.</p>
              ) : null}
            </div>
          </section>

          <section className="border border-[hsl(var(--ch-muted))] p-4">
            <h2 className="ch-label-caps mb-2 text-[hsl(var(--ch-primary))]">Permalink</h2>
            <Link className="inline-flex items-center gap-2 text-sm text-[hsl(var(--ch-primary))] underline" href={meeting.permalink}>
              <FileText className="h-4 w-4" />
              Canonical report URL
            </Link>
          </section>
        </aside>
      </div>
    </main>
  )
}
